import { call, put, takeLatest } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import {
  FetchDriver,
  UploadSSN,
  CombinedUploadSSN,
  ManualUploadSSN,
} from 'src/constants/actionTypes'
import { CombinedUploadSSNRequest, ManualUploadSSNRequest } from 'src/interfaces/ssn'
import { captureSentryError } from 'src/utils/sentry'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { GcsFileReferenceDto, uploadFileToGcs } from './api/gcsUpload'

interface UploadSSNRequest {
  ssn: string
  address: {
    streetAddress: string
    city: string
    state: string
    zipCode: string
  }
  stepId: string
}

function* onUploadSSN(action: AsyncAction<UploadSSNRequest>) {
  const { ssn, address, stepId } = action.payload

  try {
    yield call(callClientSecureApi, Endpoints.UploadSSN, {
      method: 'POST',
      body: { ssn, address, stepId },
      apiService: ApiService.Paraworks,
    })

    yield put(UploadSSN.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.ssn.onUploadSSN] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload SSN')

    yield put(UploadSSN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCombinedUploadSSN(action: AsyncAction<CombinedUploadSSNRequest>) {
  const { ssn, address, stepId, ssnFile, workAuthorization, workAuthFile } = action.payload

  try {
    let ssnCardRef: GcsFileReferenceDto | undefined
    let workAuthRef: GcsFileReferenceDto | undefined

    if (ssnFile) {
      ssnCardRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: ssnFile.name,
          mimeType: ssnFile.type,
          size: ssnFile.size,
        },
        ssnFile,
      )) as GcsFileReferenceDto
    }

    if (workAuthorization && workAuthFile) {
      workAuthRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: workAuthFile.name,
          mimeType: workAuthFile.type,
          size: workAuthFile.size,
        },
        workAuthFile,
      )) as GcsFileReferenceDto
    }

    yield call(callClientSecureApi, Endpoints.UploadSSN, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        ssn,
        address,
        ...(ssnCardRef && { ssnCard: ssnCardRef }),
        ...(workAuthRef && { workAuthorization: workAuthRef }),
      },
    })

    yield put(CombinedUploadSSN.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.ssn.onCombinedUploadSSN] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload SSN')

    yield put(CombinedUploadSSN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onManualUploadSSN(action: AsyncAction<ManualUploadSSNRequest>) {
  const { ssn, address, stepId, ssnFile, workAuthorization, workAuthFile } = action.payload

  try {
    const ssnCardRef = (yield call(
      uploadFileToGcs,
      {
        stepId,
        originalname: ssnFile.name,
        mimeType: ssnFile.type,
        size: ssnFile.size,
      },
      ssnFile,
    )) as GcsFileReferenceDto

    let workAuthRef: GcsFileReferenceDto | undefined

    if (workAuthorization && workAuthFile) {
      workAuthRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: workAuthFile.name,
          mimeType: workAuthFile.type,
          size: workAuthFile.size,
        },
        workAuthFile,
      )) as GcsFileReferenceDto
    }

    yield call(callClientSecureApi, Endpoints.UploadSSN, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        ssn,
        address,
        ssnCard: ssnCardRef,
        ...(workAuthRef && { workAuthorization: workAuthRef }),
      },
    })

    yield put(ManualUploadSSN.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.ssn.onManualUploadSSN] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload SSN')

    yield put(ManualUploadSSN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* ssnSaga() {
  yield takeLatest(UploadSSN.type.REQUEST, onUploadSSN)
  yield takeLatest(CombinedUploadSSN.type.REQUEST, onCombinedUploadSSN)
  yield takeLatest(ManualUploadSSN.type.REQUEST, onManualUploadSSN)
}
